import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SaveButton from "../../components/admin/SaveButton";
import { useUploadStatus } from "../../context/UploadStatusContext";

const empty = {
    title: "", slug: "", excerpt: "", content: "", image_url: "",
    author: "Cake Central", tags: [], is_published: true,
};

export default function AdminBlogs() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const { isUploading } = useUploadStatus();

    const load = () => api.get("/blogs?published_only=false").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (b) => { setEditing(b.id); setForm({ ...b, tags: b.tags || [] }); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        try {
            if (editing === "new") await api.post("/blogs", form);
            else await api.put(`/blogs/${editing}`, form);
            toast.success("Saved"); close(); load();
        } catch { toast.error("Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this blog?")) return;
        try { await api.delete(`/blogs/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-blogs">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-3xl">Blogs</h2>
                <button onClick={openNew} className="btn-pink" data-testid="add-blog"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((b, i) => (
                    <div key={b.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`blog-row-${i}`}>
                        <img src={b.image_url} alt={b.title} className="w-16 h-16 object-cover" />
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg">{b.title}</p>
                            <p className="text-xs text-ink-soft">{b.is_published ? "Published" : "Draft"} · /{b.slug}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => openEdit(b)} data-testid={`edit-blog-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(b.id)} data-testid={`delete-blog-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Blog</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={2} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={8} placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Cover Image" testIdPrefix="blog-image" />
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Tags (comma-separated)" value={form.tags.join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                                <span className="text-sm">Published</span>
                            </label>
                            <SaveButton />
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
